import React, { useEffect, useRef, useState } from 'react'

/**
 * ScrollReveal component to animate elements as they enter the viewport.
 * Supports direction, delay, duration, and threshold customization.
 */
export default function ScrollReveal({
  children,
  direction = 'up', // 'up' | 'down' | 'left' | 'right' | 'fade' | 'zoom'
  delay = 0, // in ms
  duration = 700, // in ms
  threshold = 0.1, // 0 to 1
  className = '',
  once = true, // only animate once
}) {
  const [isIntersecting, setIsIntersecting] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true)
          if (once && ref.current) {
            observer.unobserve(ref.current)
          }
        } else if (!once) {
          setIsIntersecting(false)
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px', // triggers slightly before entering view for better feel
      }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current)
      }
    }
  }, [threshold, once])

  // Define transform styles based on direction
  const getDirectionStyles = () => {
    if (isIntersecting) {
      return 'opacity-100 translate-x-0 translate-y-0 scale-100'
    }

    switch (direction) {
      case 'up':
        return 'opacity-0 translate-y-12'
      case 'down':
        return 'opacity-0 -translate-y-12'
      case 'left':
        return 'opacity-0 translate-x-12'
      case 'right':
        return 'opacity-0 -translate-x-12'
      case 'zoom':
        return 'opacity-0 scale-95'
      case 'fade':
      default:
        return 'opacity-0'
    }
  }

  const style = {
    transitionDuration: `${duration}ms`,
    transitionDelay: `${delay}ms`,
  }

  return (
    <div
      ref={ref}
      className={`transition-all ease-out ${getDirectionStyles()} ${className}`}
      style={style}
    >
      {children}
    </div>
  )
}
